import { ReactNode, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthStatus } from '../auth/useAuthStatus';
import ConfiguringLoader from './ConfiguringLoader';

interface ProtectedRouteProps {
    children: ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
    // Unified across Cognito (admins/SSO) AND the self-issued signer session
    // (email-only signers, #2955).
    const authStatus = useAuthStatus();
    const location = useLocation();
    /* Latched as state, not a ref (react-hooks/refs, #3347) — a render can be
       discarded or double-invoked without committing, so a ref mutated during
       render isn't guaranteed to reflect the committed value.
       Set during render, not from an effect (#3423): this is React's
       documented "adjust state while rendering" pattern — a plain conditional
       `setState` call above the early returns, guarded so it only fires the
       one render where `authStatus` actually becomes 'authenticated' (never
       re-fires on every render, and never loops, because the guard condition
       itself flips to false the instant the state updates). By the time
       authStatus transitions away from 'authenticated', this has already
       committed `true` from the prior render where it was 'authenticated'.
       This satisfies every enabled react-hooks rule: `refs` (no ref is
       touched during render — this is state), `set-state-in-render` (the
       rule's own documented exception for the conditional-during-render
       pattern), `purity` (the write is gated so it doesn't happen on every
       render — a render that doesn't hit the branch performs no write), and
       `set-state-in-effect` is moot since there is no effect left to trip it. */
    const [wasAuthenticated, setWasAuthenticated] = useState(false);

    if (authStatus === 'authenticated' && !wasAuthenticated) {
        setWasAuthenticated(true);
    }

    if (authStatus === 'configuring') {
        // Shared bounded loader (issue #2595): spinner first, then a manual
        // "Sign in" escape hatch if configuring persists. This is the COMMON
        // stuck-spinner site — an idle user on /dashboard lands here.
        return <ConfiguringLoader />;
    }

    if (authStatus !== 'authenticated') {
        // Only mark as session-expired if the user was previously authenticated in this session
        return <Navigate to="/login" state={{ from: location, sessionExpired: wasAuthenticated }} replace />;
    }

    return <>{children}</>;
}

export default ProtectedRoute;
